import React from 'react';
import { FormDialog } from '../../../form-dialog/form-dialog';
import { MultiSelect } from '../../../multi-select/multi-select';
import { SingleSelect } from '../../../single-select/single-select';
import { Checkbox } from '../../../checkbox/checkbox';
import { Row } from '../../../row/row';
import { Cell } from '../../../cell/cell';
import { ClassroomDto, EducatorDto } from '../../../../api-service/clients/auto-generated-clients';

export interface IFutureLessonsOption {
  label: string;
  description: string;
  checked: boolean;
  onToggle: () => void;
}

export interface ICourseBulkEditDialogComponentProps {
  title: string;
  description: string;
  educators: EducatorDto[];
  educatorIdsToAdd: string[];
  educatorIdsToRemove: string[];
  classrooms: ClassroomDto[];
  classroomId?: string;
  futureLessonsOption?: IFutureLessonsOption;
  isLoading: boolean;
  isSaving: boolean;
  onEducatorsToAddChange: (educators: EducatorDto[]) => void;
  onEducatorsToRemoveChange: (educators: EducatorDto[]) => void;
  onClassroomChange: (classroom?: ClassroomDto) => void;
  onConfirm: () => void;
  onClose: () => void;
}

function renderEducatorOption(optionProps: React.HTMLAttributes<HTMLLIElement>, educator: EducatorDto) {
  return (
    <li {...optionProps} key={educator.educatorId}>
      {educator.name}
    </li>
  );
}

function renderClassroomOption(optionProps: object, classroom: ClassroomDto) {
  return (
    <li {...optionProps} key={classroom.classroomId}>
      {classroom.name}
    </li>
  );
}

export function CourseBulkEditDialogComponent({
  title,
  description,
  educators,
  educatorIdsToAdd,
  educatorIdsToRemove,
  classrooms,
  classroomId,
  futureLessonsOption,
  isLoading,
  isSaving,
  onEducatorsToAddChange,
  onEducatorsToRemoveChange,
  onClassroomChange,
  onConfirm,
  onClose,
}: ICourseBulkEditDialogComponentProps) {
  const hasChanges = educatorIdsToAdd.length > 0 || educatorIdsToRemove.length > 0 || !!classroomId;

  return (
    <FormDialog
      open
      title={title}
      confirmButtonText="Udfør"
      cancelButtonText="Afbryd"
      isLoading={isSaving}
      isValid={hasChanges && !isLoading}
      onConfirmClick={onConfirm}
      onCancelClick={onClose}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <p>{description}</p>
      <Row>
        <Cell size="full">
          <MultiSelect<EducatorDto>
            label="Tilføj undervisere"
            value={educators.filter(educator => educatorIdsToAdd.includes(educator.educatorId))}
            options={educators.filter(educator => !educatorIdsToRemove.includes(educator.educatorId))}
            getOptionLabel={educator => educator.name ?? ''}
            renderOption={renderEducatorOption}
            onChange={onEducatorsToAddChange}
            disabled={isLoading}
            variant="outlined"
          />
        </Cell>
        <Cell size="full">
          <MultiSelect<EducatorDto>
            label="Fjern undervisere"
            value={educators.filter(educator => educatorIdsToRemove.includes(educator.educatorId))}
            options={educators.filter(educator => !educatorIdsToAdd.includes(educator.educatorId))}
            getOptionLabel={educator => educator.name ?? ''}
            renderOption={renderEducatorOption}
            onChange={onEducatorsToRemoveChange}
            disabled={isLoading}
            variant="outlined"
          />
        </Cell>
        <Cell size="full">
          <SingleSelect<ClassroomDto>
            label="Skift lokale"
            placeholder="Fremsøg lokale"
            value={classrooms.find(classroom => classroom.classroomId === classroomId)}
            options={classrooms}
            getOptionLabel={classroom => classroom.name}
            renderOption={renderClassroomOption}
            onChange={onClassroomChange}
            loading={isLoading}
            variant="outlined"
          />
        </Cell>
        {futureLessonsOption && (
          <Cell size="full">
            <Checkbox checked={futureLessonsOption.checked} onChange={futureLessonsOption.onToggle} label={futureLessonsOption.label} />
            <p>{futureLessonsOption.description}</p>
          </Cell>
        )}
      </Row>
    </FormDialog>
  );
}
